import { useState } from "react";
import { Link } from "react-router";
import { ArrowDown, ArrowUp } from "lucide-react";
import type { PartnerTier } from "@colab/shared";
import { TierBadge } from "@/components/shared/tier-badge";
import { formatCurrencyEUR } from "@/lib/utils";

type Row = {
  id: string;
  company_name: string;
  tier: string | null;
  total: number;
  won: number;
  arr: number;
  /** Outbound — deals Bookboost referred to or co-influenced toward the partner. */
  intros: number;
};

type SortKey = "company_name" | "total" | "won" | "arr" | "intros";
type SortDir = "asc" | "desc";

// SPA port of /app/admin/analytics/_components/PartnerPerformanceTable.tsx.

export function PartnerPerformanceTable({ rows }: { rows: Row[] }) {
  const [sort, setSort] = useState<SortKey>("arr");
  const [dir, setDir] = useState<SortDir>("desc");

  function toggle(k: SortKey) {
    if (sort === k) setDir(dir === "asc" ? "desc" : "asc");
    else {
      setSort(k);
      setDir(k === "company_name" ? "asc" : "desc");
    }
  }

  const sorted = [...rows].sort((a, b) => {
    const va = a[sort];
    const vb = b[sort];
    if (typeof va === "string" && typeof vb === "string") {
      return dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    }
    return dir === "asc" ? Number(va) - Number(vb) : Number(vb) - Number(va);
  });

  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <Th k="company_name" current={sort} dir={dir} onClick={() => toggle("company_name")}>
              Partner
            </Th>
            <th className="px-4 py-2.5">Tier</th>
            <Th
              k="total"
              current={sort}
              dir={dir}
              onClick={() => toggle("total")}
              align="right"
            >
              Total deals
            </Th>
            <Th k="won" current={sort} dir={dir} onClick={() => toggle("won")} align="right">
              Won
            </Th>
            <Th k="arr" current={sort} dir={dir} onClick={() => toggle("arr")} align="right">
              ARR (EUR)
            </Th>
            <Th
              k="intros"
              current={sort}
              dir={dir}
              onClick={() => toggle("intros")}
              align="right"
            >
              Intros
            </Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {sorted.length === 0 ? (
            <tr>
              <td
                colSpan={6}
                className="px-4 py-8 text-center text-xs text-muted-foreground"
              >
                No partner activity yet.
              </td>
            </tr>
          ) : (
            sorted.map((p) => (
              <tr key={p.id} className="hover:bg-muted/30">
                <td className="px-4 py-3">
                  <Link
                    to={`/admin/partners/${p.id}`}
                    className="font-medium text-foreground hover:text-accent"
                  >
                    {p.company_name}
                  </Link>
                </td>
                <td className="px-4 py-3">
                  {p.tier ? (
                    <TierBadge tier={p.tier as PartnerTier} />
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{p.total}</td>
                <td className="px-4 py-3 text-right tabular-nums text-status-won">
                  {p.won}
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-medium">
                  {formatCurrencyEUR(p.arr)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                  {p.intros}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  k,
  current,
  dir,
  onClick,
  align = "left",
  children,
}: {
  k: SortKey;
  current: SortKey;
  dir: SortDir;
  onClick: () => void;
  align?: "left" | "right";
  children: React.ReactNode;
}) {
  const active = current === k;
  return (
    <th
      className={`px-4 py-2.5 ${align === "right" ? "text-right" : "text-left"}`}
    >
      <button
        onClick={onClick}
        className={`inline-flex items-center gap-1 transition hover:text-foreground ${
          active ? "text-foreground" : ""
        }`}
      >
        {children}
        {active &&
          (dir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          ))}
      </button>
    </th>
  );
}
